import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { BadgeCheck, FileText } from "lucide-react";
import { money } from "@/lib/api";
import { PrivacyNotice } from "@/components/PrivacyNotice";
import { SignaturePad } from "@/components/SignaturePad";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TITLES = {
  tax_invoice: "Tax Invoice",
  gold_bullion: "Gold Bullion Invoice",
  loan: "Loan Agreement",
  buying_contract: "Buying Contract",
};

/** Public page — the customer opens this from the emailed link, no login. */
export default function SignDocument() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [reviewed, setReviewed] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/public/sign/${token}`)
      .then(({ data }) => {
        setDoc(data);
        setName(data.customer_name || "");
        if (data.already_signed) setDone(true);
      })
      .catch((e) =>
        setError(e.response?.data?.detail || "This signing link is not valid any more.")
      );
  }, [token]);

  const submit = async (image) => {
    setSaving(true);
    try {
      await axios.post(`${API}/public/sign/${token}`, { image, name, agreed });
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save your signature");
    } finally {
      setSaving(false);
    }
  };

  if (error)
    return (
      <Shell>
        <p className="text-sm text-[#9A3412]" data-testid="sign-error">
          {error}
        </p>
      </Shell>
    );

  if (!doc) return <Shell>Loading…</Shell>;

  if (done)
    return (
      <Shell>
        <div className="text-center" data-testid="sign-complete">
          <BadgeCheck className="mx-auto h-10 w-10 text-[#166534]" />
          <h2 className="mt-3 text-xl font-bold">Thank you, that's signed</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Your {TITLES[doc.doc_type]} #{doc.doc_number} has been signed. Keep a copy for your
            records.
          </p>
          <a
            href={`${API}/public/sign/${token}/pdf`}
            target="_blank"
            rel="noreferrer"
            className="mt-5 inline-flex items-center gap-2 border border-[#C5A059] px-5 py-2.5 text-sm font-semibold"
            data-testid="sign-download-pdf"
          >
            <FileText className="h-4 w-4" /> View signed PDF
          </a>
        </div>
      </Shell>
    );

  return (
    <Shell>
      <h1 className="font-invoice text-2xl font-bold">
        {TITLES[doc.doc_type]} #{doc.doc_number}
      </h1>
      <p className="mt-1 text-sm text-muted-foreground">
        {doc.branch} · {doc.doc_date}
      </p>
      <p className="mt-4 border border-[#C5A059] bg-[#FBF8F2] px-4 py-2.5 text-sm">
        {reviewed
          ? "Please type your name, accept the terms and sign below."
          : "Please review your invoice below. Nothing is signed until you choose to continue."}
      </p>

      {doc.items?.length > 0 && (
        <table className="mt-6 w-full border border-border text-sm" data-testid="sign-items">
          <tbody className="divide-y divide-border">
            {doc.items.map((it, i) => (
              <tr key={i}>
                <td className="px-3 py-2">
                  {it.description}
                  {it.weight ? ` · ${Number(it.weight).toFixed(3)} gm` : ""}
                </td>
                <td className="tabular px-3 py-2 text-right">{money(it.amount)}</td>
              </tr>
            ))}
            <tr className="bg-[#C5A059] font-bold">
              <td className="px-3 py-2">Total</td>
              <td className="tabular px-3 py-2 text-right">{money(doc.total)}</td>
            </tr>
            {doc.balance > 0.005 && (
              <tr>
                <td className="px-3 py-2">Balance due</td>
                <td className="tabular px-3 py-2 text-right">{money(doc.balance)}</td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {doc.terms?.length > 0 && (
        <details className="mt-5 border border-border p-4" data-testid="sign-terms">
          <summary className="cursor-pointer text-sm font-semibold">
            Read the terms and conditions
          </summary>
          <div className="mt-3 max-h-64 space-y-3 overflow-y-auto text-xs leading-relaxed">
            {doc.terms.map((sec, i) => (
              <div key={i}>
                <div className="font-semibold">{sec.title}</div>
                <ul className="mt-1 list-disc pl-5">
                  {(sec.points || []).map((p, j) => (
                    <li key={j}>{p}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </details>
      )}

      {!reviewed ? (
        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <a
            href={`${API}/public/sign/${token}/pdf`}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-center gap-2 border border-border px-5 py-3 text-sm font-semibold"
            data-testid="sign-preview-pdf"
          >
            <FileText className="h-4 w-4" /> Open the full invoice (PDF)
          </a>
          <button
            type="button"
            onClick={() => setReviewed(true)}
            className="flex-1 bg-[#C5A059] px-5 py-3 text-sm font-semibold text-[#0A0A0A] transition-colors duration-150 hover:bg-[#B38F4E]"
            data-testid="sign-continue-button"
          >
            I have reviewed it — continue to sign
          </button>
        </div>
      ) : (
        <>
          <div className="mt-6">
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Your full name
            </Label>
            <Input
              className="mt-1.5 h-10 rounded-none"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="sign-name-input"
            />
          </div>

          <div className="mt-4">
            <PrivacyNotice branch={doc?.branch} testid="sign-privacy" />
          </div>

          <label className="mt-4 flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-1"
              data-testid="sign-agree-checkbox"
            />
            I have read and accept the terms and conditions above.
          </label>

          <div className="mt-5">
            <SignaturePad onSave={submit} saving={saving} testidPrefix="sign" />
          </div>
        </>
      )}
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#F7F5F1] px-4 py-10">
    <div className="mx-auto max-w-2xl bg-white p-6 shadow-sm sm:p-10">
      <div className="mb-6 flex items-center gap-3 border-b border-[#C5A059] pb-4">
        <img src="/brand/logo.png" alt="Dhunna Jewellers" className="h-10 w-auto" />
        <div>
          <div className="font-invoice text-lg leading-none">DHUNNA</div>
          <div className="mt-0.5 text-[9px] font-semibold tracking-[0.3em] text-[#B38F4E]">
            JEWELLERS
          </div>
        </div>
      </div>
      {children}
    </div>
  </div>
);
